// @ts-nocheck
import React, { useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  TextField,
  IconButton,
  Tooltip,
  Typography,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Collapse,
  Paper,
  useTheme,
  alpha,
  Autocomplete,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';

import {
  useTableDispatch,
  useTableState,
} from '../../../context/TableSchemaBuilderContext';
import { ReactComponent as DropdownArrow } from '../../../../../assets/images/DropdownArrow.svg';
import StyleEditor from './StyleEditor';
import RulePreview from './RulePreview';

// Define conditions per data type
const CONDITIONS_BY_TYPE = {
  text: ['=', '!='],
  integer: ['=', '>', '<', '>=', '<=', '!='],
  float: ['=', '>', '<', '>=', '<=', '!='],
  date: ['=', '>', '<', '>=', '<=', '!='],
};

const RuleEditor = ({ rule, cellId }) => {
  const theme = useTheme();
  const [expanded, setExpanded] = useState(false);
  const { schema } = useTableState();
  const dispatch = useTableDispatch();

  const cellData = schema.cells[cellId];
  const cellValue = cellData?.value || '';
  const dataType = cellData?.dataType || 'text';

  const availableConditions = CONDITIONS_BY_TYPE[dataType];

  const handleRuleChange = useCallback(
    (updates) => {
      const updatedRule = {
        ...rule,
        ...updates,
      };

      dispatch({
        type: 'UPDATE_CONDITIONAL_RULE',
        payload: {
          cellId,
          ruleId: rule.id,
          updates: updatedRule,
        },
      });
    },
    [rule, cellId, dispatch]
  );

  const handleDeleteRule = useCallback(() => {
    dispatch({
      type: 'DELETE_CONDITIONAL_RULE',
      payload: { cellId, ruleId: rule.id },
    });
  }, [cellId, rule.id, dispatch]);

  const toggleExpand = useCallback(() => {
    setExpanded((prev) => !prev);
  }, []);

  const getRulePreviewText = useCallback(() => {
    return `${rule?.condition} ${rule?.value}`;
  }, [rule?.condition, rule?.value]);

  const handleConditionChange = useCallback(
    (e, value) => {
      handleRuleChange({ condition: value });
    },
    [handleRuleChange]
  );

  const handleValueChange = useCallback(
    (e) => {
      handleRuleChange({ value: e?.target?.value });
    },
    [handleRuleChange]
  );

  const handleStyleChange = useCallback(
    (styles) => {
      handleRuleChange({ styles });
    },
    [handleRuleChange]
  );

  const renderConditionInput = useCallback(
    (params) => (
      <TextField
        {...params}
        size="small"
        sx={{
          '&.MuiFormControl-root': {
            margin: 0,
          },
        }}
      />
    ),
    []
  );

  return (
    <Paper sx={{ mb: 1, overflow: 'hidden' }}>
      <ListItem
        onClick={toggleExpand}
        sx={{
          backgroundColor: alpha(theme?.palette?.primary?.main, 0.04),
          '&:hover': {
            backgroundColor: theme?.palette?.other?.ruleEditorHover,
          },
        }}
      >
        <ListItemText
          primary={
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                overflow: 'hidden',
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                When cell value is {getRulePreviewText()}
              </Typography>
            </Box>
          }
        />
        <ListItemSecondaryAction
          sx={{ display: 'flex', alignItems: 'center', right: '10px' }}
        >
          <Tooltip title="Delete Rule">
            <IconButton size="small" onClick={handleDeleteRule} color="error">
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        </ListItemSecondaryAction>
      </ListItem>

      <Collapse in={expanded}>
        <Box sx={{ p: 2 }}>
          <Box sx={{ mb: 2 }}>
            <Typography variant="body1" color="textSecondary">
              Current cell value:
            </Typography>
            <Typography variant="body2" color="textSecondary">
              {` ${cellValue}`}
            </Typography>
          </Box>

          <Box
            sx={{
              display: 'flex',
              gap: 1,
              alignItems: 'center',
              mb: 2,
            }}
          >
            <Autocomplete
              size="small"
              options={availableConditions}
              value={rule.condition || '='}
              onChange={handleConditionChange}
              sx={{
                minWidth: 80,
                flexShrink: 0,
              }}
              popupIcon={<DropdownArrow />}
              disableClearable
              renderInput={renderConditionInput}
            />

            <TextField
              size="small"
              value={rule.value || ''}
              onChange={handleValueChange}
              onKeyDown={(e) => {
                e.stopPropagation();

                if (e.nativeEvent?.stopImmediatePropagation) {
                  e.nativeEvent.stopImmediatePropagation();
                }
              }}
              placeholder={`Enter ${dataType.toLowerCase()} value`}
              sx={{
                flex: 1,
                minWidth: 120,
                '&.MuiFormControl-root': {
                  margin: 0,
                },
              }}
              className="conditional-rule-editor"
              spellCheck={true}
            />
          </Box>

          <StyleEditor rule={rule} onStyleChange={handleStyleChange} />

          <Box
            sx={{
              mt: 2,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <RulePreview rule={rule} />
          </Box>
        </Box>
      </Collapse>
    </Paper>
  );
};

RuleEditor.propTypes = {
  rule: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    condition: PropTypes.string,
    value: PropTypes.string,
    styles: PropTypes.shape({}).isRequired,
  }).isRequired,
  cellId: PropTypes.string.isRequired,
};

export default RuleEditor;
